/**
 * LearnedMovesModal — cartridge "Pikachu wants to learn Thunder Wave!"
 * ceremony scene.
 *
 * PR 1 of the combat-as-experience chain. Lifts the silent move-overflow
 * drop in runState.addExp into an explicit ceremony where the player
 * picks which move to forget when all 4 slots are full at level-up.
 *
 * Lifecycle (eng review Issue 1A + Issue 6A 2026-05-13):
 *   - BattleScene.battleEnd's EXP loop fires `await this.launch(...)`
 *     when a returned LevelUpEvent has `requiresChoice: true`.
 *   - Scene mounts a transparent overlay (BattleScene stays running
 *     so the choice gate animation cadence isn't disturbed).
 *   - User taps a move row → highlight (--specular-edge-emphasis 2px
 *     ring) → confirm pill slides up "Forget {Old}, learn {New}?".
 *   - Tap confirm → resolveResult({forgetIndex: N}) → close.
 *   - Tap "Don't learn {Move}" row also requires confirm pill
 *     (symmetric two-step; backdrop tap = no-op since destructive).
 *   - Scene SHUTDOWN handler (tab close / scene.stop()) resolves
 *     with `{forgetIndex: null}` — promise is GUARANTEED to settle.
 *
 * Visual spec lives in DESIGN.md Component Vocabulary entry
 * "LearnedMovesModal". The candidate-row treatment (NEW pill in
 * --matchup-good + --glass-fill-mid bg + power bar) is the first
 * --matchup-good consumer in the codebase.
 */
import Phaser from 'phaser';

export interface MoveDisplayData {
  /** @pkmn lowercase id (e.g. 'thunderwave'). Stable key. */
  id: string;
  /** Display name (e.g. 'Thunder Wave'). */
  name: string;
  /** Lowercase type id ('electric' etc.). Maps to --type-X. */
  type: string;
  /** Base power 0-150 (status moves render an empty bar via 0). */
  power: number;
  /** 0-100 accuracy (status moves with hard-coded accuracy still fit). */
  accuracy: number;
  /** Current PP / max PP. New moves start at maxPp = maxPp. */
  pp: number;
  maxPp: number;
}

export interface LearnedMovesModalData {
  /** Display name of the leveling Pokémon (already capitalized). */
  pokemonName: string;
  /** Current 4-slot move list. Indexes 0-3 are the row targets the
   *  caller will receive via `forgetIndex`. */
  currentMoves: MoveDisplayData[];
  /** The candidate move offered for adoption — distinguished in the
   *  UI by the leading 'NEW' pill + --glass-fill-mid background. */
  pendingMove: MoveDisplayData;
  /** Multi-level chain progress indicator ("Move 2 of 3"). null when
   *  this is the only forget-choice in the chain — hides the
   *  indicator entirely. */
  progress: { current: number; total: number } | null;
}

export interface LearnedMovesModalResult {
  /** Index of move to forget (0-3 maps to currentMoves[index]).
   *  null = "Don't learn {Move}" or tab-close / scene-shutdown. */
  forgetIndex: number | null;
}

/** Optional injection seam for tests + standalone smoke-tests. The
 *  BattleScene wire-up passes real LevelUpEvent data; with this seam
 *  the scene can be launched in isolation with hardcoded test moves. */
const FALLBACK_TEST_DATA: LearnedMovesModalData = {
  pokemonName: 'Pikachu',
  currentMoves: [
    { id: 'thundershock', name: 'Thunder Shock', type: 'electric', power: 40, accuracy: 100, pp: 30, maxPp: 30 },
    { id: 'growl',        name: 'Growl',         type: 'normal',   power: 0,  accuracy: 100, pp: 40, maxPp: 40 },
    { id: 'tailwhip',     name: 'Tail Whip',     type: 'normal',   power: 0,  accuracy: 100, pp: 30, maxPp: 30 },
    { id: 'quickattack',  name: 'Quick Attack',  type: 'normal',   power: 40, accuracy: 100, pp: 30, maxPp: 30 },
  ],
  pendingMove: { id: 'thunderwave', name: 'Thunder Wave', type: 'electric', power: 0, accuracy: 90, pp: 20, maxPp: 20 },
  progress: null,
};

export class LearnedMovesModal extends Phaser.Scene {
  private params: LearnedMovesModalData = FALLBACK_TEST_DATA;
  /** Set by `launch` callers via `data.onResolve`; the scene calls it
   *  exactly once and then stops. Wrapping in a getter keeps callers
   *  honest — the scene won't try to settle twice. */
  private onResolve?: (result: LearnedMovesModalResult) => void;
  private resolved = false;

  /** Root DOM container appended to #game; torn down on shutdown. */
  private root: HTMLDivElement | null = null;
  /** The currently-highlighted row (0-3 = forget candidate, 'skip' =
   *  Don't-learn row, null = no selection). Tap on a different row
   *  before confirm switches the highlight; no explicit cancel. */
  private highlighted: number | 'skip' | null = null;
  /** Row elements indexed by their semantic key. Used to update the
   *  selection ring without re-rendering the whole sheet. */
  private rowEls = new Map<number | 'skip', HTMLButtonElement>();
  /** Confirm-pill element (hidden when no highlight, contents update
   *  to match the selected row's confirm text). */
  private confirmEl: HTMLButtonElement | null = null;
  private backdropEl: HTMLDivElement | null = null;

  constructor() {
    super('LearnedMovesModal');
  }

  init(
    data: LearnedMovesModalData & {
      onResolve?: (result: LearnedMovesModalResult) => void;
    },
  ): void {
    // Defensive: callers MAY launch standalone for smoke-test. Without
    // data, fall back to the test fixture so the scaffold renders.
    if (data && data.currentMoves && data.pendingMove) {
      this.params = data;
      this.onResolve = data.onResolve;
    } else {
      this.params = FALLBACK_TEST_DATA;
      this.onResolve = undefined;
    }
    this.resolved = false;
    this.highlighted = null;
    this.rowEls.clear();
    this.confirmEl = null;
    this.root = null;
    this.backdropEl = null;
  }

  create(): void {
    this.mount();
    // Eng review Issue 1A: SHUTDOWN handler resolves the awaiting
    // promise with {forgetIndex: null} on tab-close / scene.stop().
    // Without this, BattleScene's `await launchLearnedMovesModal()`
    // would hang forever if the player closes the tab mid-modal.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.resolve({ forgetIndex: null });
      this.unmount();
    });
  }

  /** Build the DOM overlay. Glass styling from tokens.css; structure
   *  matches the DESIGN.md Component Vocabulary "LearnedMovesModal"
   *  spec. Inline-styled for tight scene-lifecycle coupling — the
   *  whole subtree disappears on shutdown. */
  private mount(): void {
    const parent = document.getElementById('game');
    if (!parent) return;

    const root = document.createElement('div');
    root.className = 'learned-moves-modal';
    Object.assign(root.style, {
      position: 'absolute',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: '900',
      pointerEvents: 'auto',
    });
    this.root = root;

    const backdrop = document.createElement('div');
    Object.assign(backdrop.style, {
      position: 'absolute',
      inset: '0',
      background: 'var(--glass-fill-thick)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    });
    // Backdrop tap = no-op (destructive irreversible action — no
    // accidental dismissals). Per design ruling 2026-05-13.
    backdrop.addEventListener('pointerdown', (ev) => ev.stopPropagation());
    root.appendChild(backdrop);
    this.backdropEl = backdrop;

    const panel = document.createElement('div');
    Object.assign(panel.style, {
      position: 'relative',
      width: 'min(440px, calc(100vw - 32px))',
      maxHeight: 'calc(100vh - 64px)',
      background: 'var(--glass-fill-mid)',
      backdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
      WebkitBackdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
      borderRadius: '12px',
      padding: '24px 20px 20px',
      boxShadow: 'var(--depth-shadow-lift)',
      color: 'var(--text-primary)',
      fontFamily: '"-apple-system", "SF Pro Display", "DM Sans Variable", "DM Sans", system-ui, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      overflow: 'hidden',
    });
    // Top specular edge.
    panel.style.boxShadow = `${panel.style.boxShadow}, inset 0 1px 0 var(--specular-edge)`;
    root.appendChild(panel);

    // Header row: title (Pokémon wants to learn Move!) + optional
    // multi-modal progress indicator (top-right).
    const headerRow = document.createElement('div');
    Object.assign(headerRow.style, {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: '12px',
      marginBottom: '4px',
    });
    const title = document.createElement('h2');
    title.textContent = `${this.params.pokemonName} wants to learn ${this.params.pendingMove.name}!`;
    Object.assign(title.style, {
      margin: '0',
      fontSize: '22px',
      fontWeight: '700',
      lineHeight: '1.2',
      color: 'var(--text-primary)',
      flex: '1 1 auto',
    });
    headerRow.appendChild(title);
    if (this.params.progress) {
      const progress = document.createElement('span');
      progress.textContent = `Move ${this.params.progress.current} of ${this.params.progress.total}`;
      Object.assign(progress.style, {
        fontSize: '11px',
        fontWeight: '500',
        fontFamily: '"DM Mono", ui-monospace, "SF Mono", monospace',
        color: 'var(--text-tertiary)',
        flex: '0 0 auto',
      });
      headerRow.appendChild(progress);
    }
    panel.appendChild(headerRow);

    // Candidate row (the NEW move) — visually distinguished.
    panel.appendChild(this.renderRow('candidate', this.params.pendingMove));
    // Incumbent rows (the 4 current moves) — tappable as forget targets.
    for (let i = 0; i < this.params.currentMoves.length; i++) {
      const move = this.params.currentMoves[i]!;
      panel.appendChild(this.renderRow(i, move));
    }
    // "Don't learn {Move}" row (symmetric two-step path).
    panel.appendChild(this.renderSkipRow());

    // Confirm pill — sticky at the bottom, hidden until a row is tapped.
    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.textContent = '';
    Object.assign(confirm.style, {
      marginTop: '8px',
      padding: '12px 16px',
      borderRadius: '8px',
      border: '1px solid var(--specular-edge-emphasis)',
      background: 'var(--matchup-good)',
      color: 'var(--text-primary)',
      fontFamily: 'inherit',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
      opacity: '0',
      visibility: 'hidden',
      transform: 'translateY(8px)',
      transition: 'opacity 200ms ease-out, transform 200ms ease-out, visibility 200ms',
    });
    confirm.addEventListener('pointerdown', (ev) => ev.stopPropagation());
    confirm.addEventListener('click', () => this.commit());
    panel.appendChild(confirm);
    this.confirmEl = confirm;

    parent.appendChild(root);
  }

  /** Build one move row. `key` distinguishes the candidate ('candidate'
   *  is render-only — not tappable in itself; it's the "wanting to learn"
   *  display), forget-target indexes 0-3, and the 'skip' row. */
  private renderRow(
    key: 'candidate' | number,
    move: MoveDisplayData,
  ): HTMLElement {
    const row = document.createElement('button');
    row.type = 'button';
    row.tabIndex = key === 'candidate' ? -1 : 0;
    const isCandidate = key === 'candidate';
    Object.assign(row.style, {
      display: 'grid',
      gridTemplateColumns: 'auto 1fr auto',
      alignItems: 'center',
      gap: '12px',
      minHeight: '56px',
      padding: '12px 16px',
      borderRadius: '8px',
      border: '2px solid transparent',
      background: isCandidate ? 'var(--glass-fill-mid)' : 'var(--glass-fill-thin)',
      color: 'var(--text-primary)',
      fontFamily: 'inherit',
      textAlign: 'left',
      cursor: isCandidate ? 'default' : 'pointer',
      transition: 'border-color 100ms ease-out, background 100ms ease-out',
    });

    // Leading column: NEW pill (candidate only) OR type pill (4px tab).
    const leading = document.createElement('span');
    Object.assign(leading.style, {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
    });
    if (isCandidate) {
      const newPill = document.createElement('span');
      newPill.textContent = 'NEW';
      Object.assign(newPill.style, {
        display: 'inline-block',
        padding: '2px 6px',
        borderRadius: '5px',
        background: 'var(--matchup-good)',
        color: 'var(--text-primary)',
        fontSize: '11px',
        fontWeight: '700',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      });
      leading.appendChild(newPill);
    }
    const typeTab = document.createElement('span');
    Object.assign(typeTab.style, {
      display: 'inline-block',
      width: '4px',
      height: '20px',
      borderRadius: '2px',
      background: `var(--type-${move.type})`,
    });
    leading.appendChild(typeTab);
    row.appendChild(leading);

    // Middle column: move name + horizontal power bar.
    const middle = document.createElement('div');
    Object.assign(middle.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    });
    const name = document.createElement('span');
    name.textContent = move.name;
    Object.assign(name.style, {
      fontSize: '17px',
      fontWeight: '500',
      lineHeight: '1.1',
      color: 'var(--text-primary)',
    });
    middle.appendChild(name);

    // Power bar — 4px tall, normalized 0-150 BP range. Status moves
    // (power=0) render as an empty track so the row height stays
    // consistent across types.
    const barTrack = document.createElement('div');
    Object.assign(barTrack.style, {
      width: '100%',
      maxWidth: '160px',
      height: '4px',
      borderRadius: '2px',
      background: 'var(--glass-fill-thin)',
      overflow: 'hidden',
    });
    const barFill = document.createElement('div');
    const powerPct = Math.min(100, (move.power / 150) * 100);
    Object.assign(barFill.style, {
      width: `${powerPct}%`,
      height: '100%',
      background: `var(--type-${move.type})`,
      opacity: '0.6',
    });
    barTrack.appendChild(barFill);
    middle.appendChild(barTrack);
    row.appendChild(middle);

    // Trailing column: BP / ACC / PP numerics in DM Mono.
    const trailing = document.createElement('span');
    Object.assign(trailing.style, {
      fontFamily: '"DM Mono", ui-monospace, "SF Mono", monospace',
      fontSize: '13px',
      fontWeight: '500',
      color: 'var(--text-secondary)',
      textAlign: 'right',
      whiteSpace: 'nowrap',
    });
    trailing.textContent = `BP ${move.power || '—'} / ACC ${move.accuracy} / PP ${move.pp}/${move.maxPp}`;
    row.appendChild(trailing);

    if (!isCandidate) {
      const idx = key as number;
      row.addEventListener('click', () => this.highlightRow(idx));
      this.rowEls.set(idx, row);
    }
    return row;
  }

  /** Build the "Don't learn {Move}" row — also a confirm-pill path. */
  private renderSkipRow(): HTMLElement {
    const row = document.createElement('button');
    row.type = 'button';
    Object.assign(row.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '48px',
      padding: '8px 16px',
      borderRadius: '8px',
      border: '2px solid transparent',
      background: 'transparent',
      color: 'var(--text-secondary)',
      fontFamily: 'inherit',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'border-color 100ms ease-out, color 100ms ease-out',
    });
    row.textContent = `Don't learn ${this.params.pendingMove.name}`;
    row.addEventListener('click', () => this.highlightRow('skip'));
    this.rowEls.set('skip', row);
    return row;
  }

  /** Switch the visual highlight + update the confirm pill text. Idempotent
   *  on a re-tap of the already-highlighted row (cheap UX — the user can
   *  re-confirm their pick by tapping the same row's confirm pill). */
  private highlightRow(key: number | 'skip'): void {
    if (this.resolved) return;
    // Clear prior selection's ring.
    for (const [k, el] of this.rowEls) {
      if (k === key) {
        el.style.borderColor = 'var(--specular-edge-emphasis)';
      } else {
        el.style.borderColor = 'transparent';
      }
    }
    this.highlighted = key;
    const pill = this.confirmEl;
    if (!pill) return;
    if (key === 'skip') {
      pill.textContent = `Don't learn ${this.params.pendingMove.name}?`;
    } else {
      const old = this.params.currentMoves[key];
      pill.textContent = old
        ? `Forget ${old.name}, learn ${this.params.pendingMove.name}?`
        : `Confirm`;
    }
    pill.style.opacity = '1';
    pill.style.visibility = 'visible';
    pill.style.transform = 'translateY(0)';
  }

  /** User tapped the confirm pill with a highlight active. Resolve +
   *  scene.stop() triggers the SHUTDOWN listener which calls unmount(). */
  private commit(): void {
    if (this.resolved) return;
    if (this.highlighted === null) return;
    const result: LearnedMovesModalResult =
      this.highlighted === 'skip' ? { forgetIndex: null } : { forgetIndex: this.highlighted };
    this.resolve(result);
    this.scene.stop();
  }

  /** Settle the awaiting promise. Guarded against double-resolution
   *  (both `commit` and `shutdown` may race on scene.stop() teardown). */
  private resolve(result: LearnedMovesModalResult): void {
    if (this.resolved) return;
    this.resolved = true;
    this.onResolve?.(result);
  }

  private unmount(): void {
    this.root?.parentElement?.removeChild(this.root);
    this.root = null;
    this.backdropEl = null;
    this.confirmEl = null;
    this.rowEls.clear();
  }
}
